import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { Profile } from 'src/app/models/Profile';
import { User } from 'src/app/models/User';

import { UserCreditInfo } from './../../models/UserCreditInfo';
import { PaginatedResult } from './../../shared/model/paginated-result';
import { ProfileService } from './../../shared/services/profile.service';
import { RechargesService } from './../../shared/services/recharges.service';
import { ADMINISTRATOR, CUSTOMER_SUCCESS, UserService } from './../../shared/services/user.service';
import { AuthService } from 'src/app/shared/services/auth.service';


@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {

  page = 1;
  perPage = 50;
  totalCreditsInfo: UserCreditInfo | null = null;
  result: PaginatedResult<User[]> | null = null;
  loading = false;
  billing: number = 0;

  signatureBilling: number = 0;
  callBilling: number = 0;
  smsBilling: number = 0;

  billingMonthStart: string = '';
  billingMonthEnd: string = '';
  profileId: string = '';
  profiles$: Observable<Profile[]> = of([]);
  billingMonths: any[] = [];
  searched = false;

  user: User | null = null;

  startDate: Date | null = null;
  endDate: Date | null = null;

  private defaultPeriodApplied = false;

  destroyed = new Subject<void>();

  get customer_success(): boolean {
    return this.user?.profile_id === CUSTOMER_SUCCESS;
  }

  get administrator() {
    return this.user?.profile_id === ADMINISTRATOR;
  }

  get billingMonthsBr() {
    return this.billingMonths?.map(bm => {
      bm.month_year = bm
        .year_month
        .split('-')
        .reverse()
        .join('/');

      return bm;
    });
  }

 constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private rechargesService: RechargesService,
    private authService: AuthService,
    private profileService: ProfileService
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();

    if (!this.administrator) {
      this.router.navigate(['/dashboard']);
      return;
    }


    this.route.queryParams
      .pipe(takeUntil(this.destroyed))
      .subscribe(params => {
        this.page = +params.page || 1;
        this.perPage = +params.per_page || this.perPage;
        this.billingMonthStart = params.billing_month_start ?? "";
        this.billingMonthEnd = params.billing_month_end ?? ""
        this.profileId = params.profile_id ?? "";
        this.startDate = this.parseDate(this.billingMonthStart);
        this.endDate = this.parseDate(this.billingMonthEnd);
        this.searched = params.searched === 'true';

        // Ao acessar a tela, sugere o período do mês atual (primeiro dia até hoje).
        // Só na primeira carga, para o botão Limpar conseguir deixar os campos vazios.
        if (!this.defaultPeriodApplied) {
          this.defaultPeriodApplied = true;
          this.startDate = this.startDate ?? this.firstDayOfCurrentMonth();
          this.endDate = this.endDate ?? this.currentDate();
        }

        if (!this.searched) {
          this.clearResults();
          return;
        }

        this.listPaginated();
      });

    this.rechargesService.billingMonths()
      .pipe(takeUntil(this.destroyed))
      .subscribe(billingMonths => this.billingMonths = billingMonths)

    this.profiles$ = this.profileService.listAll();
  }

  private firstDayOfCurrentMonth(): Date {
    const today = new Date();

    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  private currentDate(): Date {
    const today = new Date();

    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  /** Converte 'YYYY-MM-DD' em uma data no fuso local (new Date('YYYY-MM-DD') interpreta como UTC e volta 1 dia). */
  private parseDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);

    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  /** Converte a data para 'YYYY-MM-DD' usando o fuso local (toISOString converte para UTC e pode mudar o dia). */
  private formatDate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }


  listPaginated(): void {

    this.loading = true;

    this.userService
      .listCreditsPaginated(this.page, this.perPage, this.billingMonthStart, this.billingMonthEnd, this.profileId)
      .pipe(takeUntil(this.destroyed))
      .subscribe(result => {
        this.loading = false;
        this.result = result;

        let credits = 0;
        let creditsBilling = 0;
        let signatureBilling = 0;
        let totalReserved = 0;
        let smsBilling = 0;
        let callBilling = 0;

        this.result.data.forEach(user => {
          if (user.credit_info.credits) {
            credits += user.credit_info.credits;
            creditsBilling += user.credit_info.credits_billing;
            signatureBilling += user.credit_info.signature_billing;
            totalReserved += user.credit_info.total_reserved;
            smsBilling += user.credit_info.sms_billing;
            callBilling += user.credit_info.call_billing;
          }
        });

        this.totalCreditsInfo = {
          credits,
          credits_billing: creditsBilling,
          signature_billing: signatureBilling,
          sms_billing: smsBilling,
          call_billing: callBilling,
          total_sent: 0,
          total_reserved: totalReserved,
          total_contracted: creditsBilling + signatureBilling
        };
      });


    this.rechargesService
      .totalBilling(this.billingMonthStart, this.billingMonthEnd, this.profileId)
      .pipe(takeUntil(this.destroyed))
      .subscribe(t => {
        this.signatureBilling = t.signature_billing;
        this.smsBilling = t.sms_billing;
        this.callBilling = t.call_billing;
        this.billing = this.signatureBilling + this.smsBilling + this.callBilling;
      });
  }

  search(): void {
    this.pageChanged(1);
  }

  clearFilters(): void {
    this.profileId = '';
    this.startDate = null;
    this.endDate = null;
    this.billingMonthStart = '';
    this.billingMonthEnd = '';
    this.page = 1;
    this.searched = false;
    this.clearResults();
    this.router.navigate(['/reports']);
  }

  private clearResults(): void {
    this.result = null;
    this.totalCreditsInfo = null;
    this.billing = 0;
    this.signatureBilling = 0;
    this.callBilling = 0;
    this.smsBilling = 0;
  }

  pageChanged(page: number): void {
    const queryParams: any = { page, per_page: this.perPage, searched: true };

    if (this.profileId) {
      queryParams['profile_id'] = this.profileId;
    }

    if (this.startDate) {
      queryParams['billing_month_start'] = this.formatDate(this.startDate);
    }
    if (this.endDate) {
      queryParams['billing_month_end'] = this.formatDate(this.endDate);
    }

    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      return;
    }

    this.router.navigate(['/reports'], { queryParams });
  }

  download(): void {
    const start = this.startDate ? this.formatDate(this.startDate) : '';
    const end = this.endDate ? this.formatDate(this.endDate) : '';

    this.rechargesService.download(start, end, this.profileId);
  }

  getTotalBilling(creditInfo: UserCreditInfo): number {
    return creditInfo.signature_billing +
      creditInfo.sms_billing +
      creditInfo.call_billing;
  }
}
